'use client'

import { Analytics } from '@vercel/analytics/next'
import { SpeedInsights } from '@vercel/speed-insights/next'
import { useEffect, useState } from 'react'
import {
  ADVERTISING_CONSENT_CHANGED_EVENT,
  ADVERTISING_MEASUREMENT_ACTIVATE_FUNCTION,
  ensureBrowserAdvertisingConsent,
  isBrowserAdvertisingMeasurementGranted,
} from '@/lib/consent/advertising-consent'
import { MetaPixel } from './MetaPixel'
import { MicrosoftUet } from './MicrosoftUet'
import { OpenAiPixel } from './OpenAiPixel'
import { microsoftUetBootstrapScript } from '@/lib/analytics/microsoft-uet'

export type AdvertisingMeasurementConfig = {
  gtmId?: string
  clarityId?: string
  metaPixelId?: string
  microsoftUetTagId?: string
  openAiPixelId?: string
  openAiPixelDebug: boolean
}

type ActivationWindow = Window & {
  [ADVERTISING_MEASUREMENT_ACTIVATE_FUNCTION]?: () => void
}

function installInlineScript(id: string, source: string): void {
  if (document.getElementById(id)) return
  const script = document.createElement('script')
  script.id = id
  script.text = source
  document.body.appendChild(script)
}

export function useAdvertisingMeasurementGrant(): boolean {
  const [granted, setGranted] = useState(false)

  useEffect(() => {
    const update = () => {
      ensureBrowserAdvertisingConsent()
      setGranted(isBrowserAdvertisingMeasurementGranted())
    }
    update()
    window.addEventListener(ADVERTISING_CONSENT_CHANGED_EVENT, update)
    return () => window.removeEventListener(ADVERTISING_CONSENT_CHANGED_EVENT, update)
  }, [])

  return granted
}

export function AdvertisingMeasurement({
  config,
  granted,
}: {
  config: AdvertisingMeasurementConfig
  granted: boolean
}) {
  useEffect(() => {
    if (!granted) return

    if (config.gtmId) {
      ;(window as ActivationWindow)[ADVERTISING_MEASUREMENT_ACTIVATE_FUNCTION]?.()
    }

    if (config.clarityId) {
      installInlineScript(
        'cg-clarity-bootstrap',
        `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${config.clarityId}");`,
      )
    }

    if (config.metaPixelId) {
      installInlineScript(
        'cg-meta-pixel-bootstrap',
        `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${config.metaPixelId}');fbq('track','PageView');`,
      )
    }

    if (config.microsoftUetTagId) {
      installInlineScript(
        'cg-microsoft-uet-bootstrap',
        microsoftUetBootstrapScript(config.microsoftUetTagId),
      )
    }

    if (config.openAiPixelId) {
      installInlineScript(
        'cg-openai-pixel-bootstrap',
        `!function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)}(window,document,"script","https://bzrcdn.openai.com/sdk/oaiq.min.js");oaiq("init",{pixelId:"${config.openAiPixelId}",debug:${config.openAiPixelDebug}});oaiq("measure","page_viewed",{type:"contents"});`,
      )
    }
  }, [config, granted])

  if (!granted) return null

  return (
    <>
      {config.metaPixelId && <MetaPixel />}
      {config.microsoftUetTagId && <MicrosoftUet />}
      {config.openAiPixelId && <OpenAiPixel />}
      {config.gtmId && <Analytics />}
      {config.gtmId && <SpeedInsights />}
    </>
  )
}
